import React, { useEffect, useState, useRef } from "react"
import { Link } from "react-router-dom"
// icons
import { IconContext } from "react-icons/lib"
import { TbBuildingBank, TbUrgent, TbLayoutSidebarLeftCollapse } from "react-icons/tb"
import { AiOutlineHome } from "react-icons/ai"
import { IoBookOutline, IoChevronDown, IoChevronUp } from "react-icons/io5"
import { TbActivityHeartbeat } from "react-icons/tb"
import { FaRegFile, FaStairs } from "react-icons/fa6"
import { TbAntennaBars1 } from "react-icons/tb";
import { FiSettings } from "react-icons/fi"
import { useCustomer } from "../../contexts/CustomerContext"
import { useGetMenu } from "@murphy-frontend/common/api/Menu/Queries"
import { IMenuItem } from "@murphy-frontend/common/interfaces/IMenuApi"
import MyMurphyLogo from "@murphy-frontend/web-core/assets/img/mymurphy_logo.svg"
import { useUserPreferences } from "../../contexts/UserPreferencesContext"
import LanguageDropdown from "./LanguageDropdown"
import LogoutButton from "./LogoutButton"
import TimezoneDropdown from "./TimezoneDropdown"
import UserDropdown from "./UserDropdown"

interface MenuProps {
	subMenuActive?: number
	active: number
}

interface MenuItems extends IMenuItem {
	isToggled?: boolean
	active?: boolean
}

interface SetActiveProps {
	items: MenuItems[]
	active: number
	subMenuActive?: number
}

const Menu = ({ subMenuActive, active }: MenuProps) => {
	const { customer } = useCustomer()
	const { collapsed, setCollapsed } = useUserPreferences()

	const { data: menuData, isLoading } = useGetMenu(customer?.Id)
	const myRef = useRef<HTMLLIElement>(null)

	const _setActive = ({ items, active, subMenuActive }: SetActiveProps) => {
		if (isLoading) return []
		const itemsList = items.map((menuItem: MenuItems) => {
			let newItem: MenuItems = { ...menuItem, isToggled: false }

			newItem.active = active === newItem.id

			if (!menuItem.subMenu?.length || !subMenuActive) return newItem

			if (active === newItem.id) newItem.isToggled = true

			newItem.subMenu = newItem.subMenu.map(submenuItem => ({
				...submenuItem,
				active: submenuItem.id === subMenuActive
			}))
			return newItem
		})

		return itemsList
	}

	const [navItems, setNavItems] = useState<MenuItems[]>(_setActive({ items: menuData, active, subMenuActive }))
	const [showSettings, setShowSettings] = useState(false)

	const handleClickInside = () => {
		setShowSettings(true)
	}

	function handleClickOutside(e: MouseEvent) {
		if (!myRef.current.contains(e.target as HTMLInputElement)) {
			setShowSettings(false)
		}
	}

	useEffect(() => {
		document.addEventListener("click", handleClickOutside)
		return () => document.removeEventListener("click", handleClickOutside)
	}, [])

	useEffect(() => {
		if (!isLoading) setNavItems(_setActive({ items: menuData, active, subMenuActive }))
	}, [isLoading, active, subMenuActive])

	const _handleClick = (e: React.MouseEvent<HTMLElement>, item: MenuItems) => {
		e.persist()
		setNavItems(navItems => navItems.map(_ => (_.id === item.id ? { ..._, isToggled: false } : _)))
	}

	const handleToggle = (id: number) => {
		setNavItems(navItems => navItems.map(_ => (_.id === id ? { ..._, isToggled: !_.isToggled } : _)))
	}

	const addActiveClass = (classes: string, active: boolean) => {
		if (active) return classes + " sidebar-active"
		else return classes
	}

	const addSubActiveClass = (classes: string, subMenuId: number, activeId: number) => {
		if (subMenuActive === subMenuId && activeId === active) return classes + " sidebar-submenu-active"
		else return classes
	}

	const icon = (id: number) => {
		switch (id) {
			case 1:
				return (
					<IconContext.Provider value={{ className: "sidebar-icon" }}>
						<AiOutlineHome />
					</IconContext.Provider>
				)
			case 2:
				return (
					<IconContext.Provider value={{ className: "sidebar-icon" }}>
						<TbUrgent />
					</IconContext.Provider>
				)
			case 3:
				return (
					<IconContext.Provider value={{ className: "sidebar-icon" }}>
						<TbBuildingBank />
					</IconContext.Provider>
				)
			case 4:
				return (
					<IconContext.Provider value={{ className: "sidebar-icon" }}>
						<IoBookOutline />
					</IconContext.Provider>
				)
			case 5:
				return (
					<IconContext.Provider value={{ className: "sidebar-icon" }}>
						<TbActivityHeartbeat />
					</IconContext.Provider>
				)
			case 6:
				return (
					<IconContext.Provider value={{ className: "sidebar-icon" }}>
						<FaStairs />
					</IconContext.Provider>
				)
			case 7:
				return (
					<IconContext.Provider value={{ className: "sidebar-icon" }}>
						<FaRegFile />
					</IconContext.Provider>
				)
			case 8:
				return (
					<IconContext.Provider value={{ className: "sidebar-icon" }}>
						<TbAntennaBars1 />
					</IconContext.Provider>
				)
			default:
				return null
		}
	}

	const addCollapsedClass = (collapse: boolean, classname: string): string => {
		if (collapse) {
			return `${classname}--collapsed`
		}
		return classname
	}

	return (
		<aside className={addCollapsedClass(collapsed, "sidebar-left-panel")}>
			<ul className={addCollapsedClass(collapsed, "sidebar-menu-wrapper")}>
				{collapsed ? (
					<li className='sidebar-logo-portal--collapsed'>
						<IconContext.Provider value={{ className: "sidebar-collapse-icon" }}>
							<TbLayoutSidebarLeftCollapse onClick={() => setCollapsed(false)} />
						</IconContext.Provider>
					</li>
				) : (
					<li className='sidebar-logo-portal'>
						<Link to='/portal' className='sidebar-logo-link'>
							<MyMurphyLogo alt='logo' className='sidebar-logo' />
						</Link>
						<IconContext.Provider value={{ className: "sidebar-collapse-icon" }}>
							<TbLayoutSidebarLeftCollapse onClick={() => setCollapsed(true)} />
						</IconContext.Provider>
					</li>
				)}
				{navItems.map((item, index) => {
					return (
						<li key={`menu-item-${index}`} className={addActiveClass("sidebar-list-item", item.active)}>
							{item.subMenu?.length ? (
								<>
									{collapsed ? (
										<div
											className={addCollapsedClass(collapsed, "sidebar-title")}
											onClick={() => handleToggle(item.id)}
										>
											<Link
												to={item.path}
												key={item.id}
												onClick={e => _handleClick(e, item)}
												className={addCollapsedClass(
													collapsed,
													"sidebar-link sidebar-link-nested"
												)}
											>
												{icon(item.id)}
											</Link>
										</div>
									) : (
										<div
											className={addCollapsedClass(collapsed, "sidebar-title")}
											onClick={() => handleToggle(item.id)}
										>
											<div className='sidebar-text-wrapper'>
												<Link
													to={item.path}
													key={item.id}
													onClick={e => _handleClick(e, item)}
													className={addCollapsedClass(
														collapsed,
														"sidebar-link sidebar-link-nested"
													)}
												>
													{icon(item.id)}
													<div className='sidebar-text'>{item.title}</div>
												</Link>
												<IconContext.Provider value={{ className: "btn-icon" }}>
													{item.isToggled ? <IoChevronUp /> : <IoChevronDown />}
												</IconContext.Provider>
											</div>
										</div>
									)}
									{!collapsed && (
										<div
											className={item.isToggled ? "sidebar-accordion" : "sidebar-accordion--hide"}
										>
											{item.subMenu.map(sub => (
												<ul
													key={sub.id}
													className={addSubActiveClass(
														"sidebar-list-item sidebar-ul-nested",
														sub.id,
														item.id
													)}
												>
													<li className='sidebar-list-nested'>
														<Link
															to={sub.path}
															key={sub.id}
															target={sub.external ? "_blank" : "_self"}
															onClick={e => _handleClick(e, sub)}
															className='sidebar-link sidebar-link-nested'
														>
															{sub.title}
														</Link>
													</li>
												</ul>
											))}
										</div>
									)}
								</>
							) : (
								<Link
									to={item.path}
									key={item.id}
									onClick={e => _handleClick(e, item)}
									className='sidebar-link'
								>
									<div className={addCollapsedClass(collapsed, "sidebar-title")}>
										{icon(item.id)}
										{!collapsed && item.title}
									</div>
								</Link>
							)}
						</li>
					)
				})}
				<li
					key={"settings"}
					className={"sidebar-list-item sidebar-settings-item"}
					onClick={handleClickInside}
					ref={myRef}
				>
					<div className={addCollapsedClass(collapsed, "sidebar-title")}>
						<IconContext.Provider value={{ className: "sidebar-icon" }}>
							<FiSettings />
						</IconContext.Provider>
						{`${collapsed ? "" : "Settings"}`}
						{showSettings && (
							<ul className={"sidebar-settings"}>
								<LanguageDropdown />
								<TimezoneDropdown />
								<LogoutButton />
							</ul>
						)}
					</div>
				</li>
				<li key={"user"} className={"sidebar-list-item sidebar-user-item"}>
					<UserDropdown />
				</li>
			</ul>
		</aside>
	)
}

export default Menu